// A hovered card's box in the grid canvas's own coordinate space, plus the lane it sits in.
export interface ConnectorBox {
  laneId: string;
  left: number;
  right: number;
  top: number;
  bottom: number;
}

// The visible box a card is allowed to be drawn at — its cell.
export interface ConnectorBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface ConnectorPoint {
  x: number;
  y: number;
}

export interface ConnectorChain {
  laneId: string;
  path: string;
  points: ConnectorPoint[];
}

// A week holding more blocks than fit scrolls inside its own cell, and a card scrolled out of
// that list still reports its full rect — hundreds of pixels below the cell, over the capacity
// rows underneath. Clamping to the cell parks the endpoint on the edge the card is hiding
// behind, so the line stops at the boundary instead of pointing at something invisible.
export function clampToBounds(box: ConnectorBox, bounds: ConnectorBounds): ConnectorBox {
  return {
    laneId: box.laneId,
    left: clamp(box.left, bounds.left, bounds.right),
    right: clamp(box.right, bounds.left, bounds.right),
    top: clamp(box.top, bounds.top, bounds.bottom),
    bottom: clamp(box.bottom, bounds.top, bounds.bottom),
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// The shortest horizontal run out of a card before the line turns. Only bites when two cards
// share a column, where a turn at their midpoint would fall inside the cards themselves.
const MIN_STUB = 8;

// Joins one PO's cards into a chain per lane, left to right: each card's right edge routes to
// the next card's left edge through a vertical run between the two columns, so the line reads
// as a route across the weeks rather than a diagonal over them. Lanes are kept apart — a PO
// scheduled under two vendors chains within each, never between them — and a lane holding a
// single card contributes nothing.
export function connectorChains(boxes: ConnectorBox[]): ConnectorChain[] {
  const lanes = new Map<string, ConnectorBox[]>();
  boxes.forEach((box) => {
    const lane = lanes.get(box.laneId);
    if (lane) lane.push(box);
    else lanes.set(box.laneId, [box]);
  });

  return [...lanes.entries()]
    .map(([laneId, lane]) => chainLane(laneId, lane))
    .filter((chain) => chain.path.length > 0);
}

function chainLane(laneId: string, lane: ConnectorBox[]): ConnectorChain {
  const ordered = [...lane].sort((first, second) => first.left - second.left);
  const segments: string[] = [];
  const points: ConnectorPoint[] = [];

  ordered.slice(0, -1).forEach((from, index) => {
    const to = ordered[index + 1];
    const start = { x: round(from.right), y: round(midY(from)) };
    const end = { x: round(to.left), y: round(midY(to)) };
    const turn = round(Math.max((from.right + to.left) / 2, from.right + MIN_STUB));
    segments.push(`M ${start.x} ${start.y} H ${turn} V ${end.y} H ${end.x}`);
    points.push(start, end);
  });

  return { laneId, path: segments.join(' '), points };
}

function midY(box: ConnectorBox): number {
  return (box.top + box.bottom) / 2;
}

// A tenth of a pixel is finer than a 1.5px dashed stroke can show, and keeps the path
// stable enough that a scroll does not rewrite every coordinate.
function round(value: number): number {
  return Math.round(value * 10) / 10;
}
